/**
 * Calibration — the pure math behind the calibration dashboard (/calibration),
 * shared by the app (src/lib/itemDeliveryService.ts and
 * src/lib/valueOutcomesService.ts re-export it) and the public API
 * (GET /v1/calibration). No imports, so the Vite app and the Deno edge
 * function both load this one copy.
 */

/** The delivery fields the stats read, in the app's camelCase shape. */
export interface CalibrationDeliveryInput {
  deliveryType: string;
  scoreAccuracy: number | null;
  predictedScores: { toolName?: string | null } | null;
}

// Summary stats for the calibration dashboard.
export interface CalibrationStats {
  totalDeliveries: number;
  shippedCount: number;
  partialCount: number;
  cancelledCount: number;
  deliveryRate: number;           // (shipped + partial) / all deliveries
  averageAccuracy: number | null; // mean score_accuracy across items that have it
  frameworkBreakdown: Array<{
    toolName: string;
    count: number;
    averageAccuracy: number | null;
  }>;
}

export function computeCalibrationStats(deliveries: readonly CalibrationDeliveryInput[]): CalibrationStats {
  const shippedCount = deliveries.filter((d) => d.deliveryType === 'shipped').length;
  const partialCount = deliveries.filter((d) => d.deliveryType === 'partial').length;
  const cancelledCount = deliveries.filter((d) => d.deliveryType === 'cancelled').length;
  const deliveryRate = deliveries.length > 0
    ? (shippedCount + partialCount) / deliveries.length
    : 0;

  const withAccuracy = deliveries.filter((d) => d.scoreAccuracy != null);
  const averageAccuracy = withAccuracy.length > 0
    ? withAccuracy.reduce((sum, d) => sum + d.scoreAccuracy!, 0) / withAccuracy.length
    : null;

  // Group by framework
  const byTool = new Map<string, CalibrationDeliveryInput[]>();
  for (const d of deliveries) {
    if (!d.predictedScores?.toolName) continue;
    const key = d.predictedScores.toolName;
    if (!byTool.has(key)) byTool.set(key, []);
    byTool.get(key)!.push(d);
  }

  const frameworkBreakdown = Array.from(byTool.entries()).map(([toolName, items]) => {
    const scored = items.filter((d) => d.scoreAccuracy != null);
    return {
      toolName,
      count: items.length,
      averageAccuracy: scored.length > 0
        ? scored.reduce((s, d) => s + d.scoreAccuracy!, 0) / scored.length
        : null,
    };
  });

  return {
    totalDeliveries: deliveries.length,
    shippedCount,
    partialCount,
    cancelledCount,
    deliveryRate,
    averageAccuracy,
    frameworkBreakdown,
  };
}

// ── Accepted values against what reality said ───────────────────────────────

/** The value-outcome fields the summary reads, in the app's camelCase shape. */
export interface ValueOutcomeInput {
  attributeKey: string;
  acceptedOrigin: string;
  /** Distance on the scale between accepted and realized; null when either could not be placed. */
  error: number | null;
}

export interface OriginSummary {
  count: number;
  /** Mean absolute error on the scale, over rows where the error is known. */
  meanError: number | null;
  /** Share of rows whose accepted value equalled the realized one. */
  exactRate: number | null;
}

export interface AttributeOutcomeSummary {
  attributeKey: string;
  all: OriginSummary;
  agent: OriginSummary;
  person: OriginSummary;
}

function summarize(rows: readonly ValueOutcomeInput[]): OriginSummary {
  const known = rows.filter((r) => r.error != null);
  const meanError = known.length ? known.reduce((s, r) => s + (r.error as number), 0) / known.length : null;
  const exactRate = known.length ? known.filter((r) => r.error === 0).length / known.length : null;
  return { count: rows.length, meanError, exactRate };
}

/** Per attribute: everyone, the agent, and people side by side. */
export function summarizeValueOutcomes(rows: readonly ValueOutcomeInput[]): AttributeOutcomeSummary[] {
  const byAttribute = new Map<string, ValueOutcomeInput[]>();
  for (const r of rows) {
    const list = byAttribute.get(r.attributeKey) ?? [];
    list.push(r);
    byAttribute.set(r.attributeKey, list);
  }
  return [...byAttribute.entries()]
    .map(([attributeKey, list]) => ({
      attributeKey,
      all: summarize(list),
      agent: summarize(list.filter((r) => r.acceptedOrigin === 'agent')),
      person: summarize(list.filter((r) => r.acceptedOrigin === 'person')),
    }))
    .sort((a, b) => b.all.count - a.all.count);
}
